// Los links para salir del navegador de Instagram, Facebook y compañía sin perder la atribución.
//
// Google no siempre deja iniciar sesión dentro de esos navegadores: Nacho lo midió en PostHog y
// dentro de Instagram falla más o menos a la mitad, dentro de Facebook Android casi siempre.
// El registro muestra un aviso (`AvisoGoogleEnApp`) que ofrece abrir Atlas en el navegador;
// estos son los links que usa (ADR-2026-10-20-google-avisa-en-instagram-sin-frenar).
// Quién está dentro de qué app lo decide `navegadorEmbebido`.
//
// Todo lo de este archivo es puro, para poder testearlo.

import { UTM_KEYS } from './signupAttribution';

/** Lo que la persona tiene guardado de su llegada (las claves `atlas:*` de `signupAttribution`). */
export type AtribucionGuardada = Partial<Record<string, string | null>>;

/** El fbclid sale del `_fbc` (`fb.1.<timestamp>.<fbclid>`): con él, el navegador nuevo rearma la cookie. */
export function fbclidDelFbc(fbc: string | null | undefined): string | null {
  const partes = (fbc ?? '').split('.');
  if (partes.length < 4 || partes[0] !== 'fb') return null;
  const fbclid = partes.slice(3).join('.');
  return fbclid || null;
}

/**
 * La URL actual con la atribución guardada pegada en el query.
 *
 * Al pasar a Chrome o Safari, el navegador nuevo arranca VACÍO: lo que este guardó en
 * localStorage (de qué anuncio vino, el código de afiliado) no viaja. Si no va en el link,
 * arreglar el login rompía la atribución que se arregló en la landing. Se SUMA lo que falta
 * y nunca se pisa lo que la URL ya trae (el alta por link lleva su plan, país y `sid`).
 */
export function urlConAtribucion(href: string, guardado: AtribucionGuardada): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  const sumar = (param: string, valor: string | null | undefined) => {
    if (valor && !url.searchParams.has(param)) url.searchParams.set(param, valor);
  };
  for (const key of UTM_KEYS) sumar(key, guardado[key]);
  sumar('ref', guardado.ref);
  sumar('fbclid', fbclidDelFbc(guardado.fbc));
  return url.toString();
}

/**
 * Link que en Android le pide al sistema abrir la URL en Chrome. Si Chrome no está, el
 * sistema cae a `browser_fallback_url` (la misma URL, en el navegador que haya).
 * Instagram y Facebook no garantizan respetarlo: por eso siempre acompañan los pasos a mano.
 */
export function intentParaChrome(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const esquema = url.protocol.replace(':', '');
  const sinEsquema = `${url.host}${url.pathname}${url.search}${url.hash}`;
  return `intent://${sinEsquema}#Intent;scheme=${esquema};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url.toString())};end`;
}
